/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostListener } from '@angular/core';
import { FormMessageService } from '@gh/core-mls';
import { coalesce, hasClosest, isEmptyValue, isNotEmpty, isNotNil } from '@gh/core-util';
import { Column, GridApi, ICellRendererParams, RowNode } from 'ag-grid-community';
import { constant, first, stubFalse } from 'lodash';
import { CellParams } from '../../../state';
import { EditableCellModel, EditableCellModelFactory } from '../../../state/grid-state.interfaces';
import { GridCellRendererComponent } from '../../grid-component.interfaces';

export interface GridCellRendererEditorTrigger {
  iconName: string;
  tooltip: string;
}

/**
 * Cell renderer for editable cell.
 * It supports the following set of properties
 * <ul>
 *   <li> - <code>modelFactory</code> is an instance of {@link EditableCellModelFactory}
 *        which creates {@link EditableCellModel}
 *   </li>
 *   <li> - <code>hasContentTooltip</code> allows to show tooltip with cell content</li>
 *   <li> - <code>hasSelectTrigger</code> allows to show select-like trigger.
 *        When <code>clearable</code> property is set, select trigger will be hidden for not empty cell</li>
 *   <li> - <code>clearable</code>. When this property is set and cell is not empty,
 *        clear icon is shown on the right side of cell content.
 *        If cell value is required clear icon is not displayed.
 *        When user clicks on clear icon <code>onClear</code> handler is triggered</li>
 *   <li> - <code>forceClearable</code>. The same option as <code>clearable</code>.
 *        The only difference that clear icon is displayed even when cell value is required</li>
 *   <li> - <code>onClear</code> defines handler for clicking on clear icon</li>
 *   <li> - <code>singleClickEdit</code>. When this property is set, single click on cell content starts editing</li>
 *   <li> - <code>useEditableAsEnabled</code> controls whether editable state is treated like enabled.
 *        This affects only cell UI. When property is set, cell content is always drawn as underlined
 *        and with select trigger (if any), independently on editable state</li>
 *   <li> - <code>hasAlternativeText</code> is a function that defines whether alternative text should be displayed.
 *        Alternative text is displayed instead of actual cell content as read-only italic text</li>
 *   <li> - <code>getAlternativeText</code> is a function that returns alternative text.
 *        This function is called only when <code>hasAlternativeText</code> returns true</li>
 *   <li> - <code>editorTrigger</code> allows to display trigger for embedded editor.</li>
 *   <li> - <code>onEditorOpen</code> defines handler triggered when user clicks on editorTrigger</li>
 *   <li> - <code>align</code> defines text alignment mode: left, right or center</li>
 * </ul>
 */
@Component({
  selector: 'gh-grid-editable-cell',
  template: require('./grid-editable-cell-renderer.component.html'),
  styles: [require('./grid-editable-cell-renderer.component.scss')],
  changeDetection: ChangeDetectionStrategy.OnPush,
  // tslint:disable-next-line:use-host-property-decorator
  host: {
    '[class.grid-editable-cell]': 'true',
    '[class.m-disabled]': '!enabled',
    '[class.is-editable]': 'displayAsEditable',
    '[class.is-alternative]': 'displayAlternativeText',
    '[class.is-invalid]': 'invalid',
  },
})
export class GridEditableCellRendererComponent implements GridCellRendererComponent {
  align: string;
  hasSelectTrigger: boolean;
  clearable: boolean;
  forceClearable: boolean;
  params: any;
  alternativeText?: string;
  editorTrigger?: GridCellRendererEditorTrigger;

  private _message: string;
  private model: EditableCellModel;
  private valueFormatted: string;
  private modelFactory: EditableCellModelFactory;
  // whether to display tooltip with cell content on hover
  private hasContentTooltip = false;
  private singleClickEdit = false;
  private useEditableAsEnabled = false;

  private api: GridApi;
  private column: Column;
  private node: RowNode;
  private data: any;
  private onClear: (params: CellParams) => void;
  private onEditorOpen: (params: CellParams) => void;
  private hasAlternativeText: (params: CellParams) => boolean;
  private getAlternativeText: (params: CellParams) => string;

  constructor(private messageService: FormMessageService,
              private changeDetectorRef: ChangeDetectorRef) {
  }

  get displayEditorTrigger(): boolean {
    return this.editable && isNotNil(this.editorTrigger);
  }

  get displaySelectTrigger(): boolean {
    if (this.displayAlternativeText) {
      return false;
    } else if (this.editable) {
      return this.displayClearTrigger ? false : this.hasSelectTrigger;
    } else {
      return false;
    }
  }

  get displayClearTrigger(): boolean {
    if (this.displayAlternativeText) {
      return false;
    } else if (this.editable) {
      return this.clearable && (this.forceClearable || !this.model.required) && !isEmptyValue(this.model.value);
    } else {
      return false;
    }
  }

  get displayAsEditable(): boolean {
    return this.displayAlternativeText ? false : this.editable;
  }

  get displayAlternativeText(): boolean {
    return isNotEmpty(this.alternativeText);
  }

  get editable(): boolean {
    return this.useEditableAsEnabled ? true : this.model.editable;
  }

  get enabled(): boolean {
    return this.useEditableAsEnabled ? this.model.editable : true;
  }

  get invalid(): boolean {
    return this.model.invalid;
  }

  get value(): string {
    return this.displayAlternativeText ? (this.alternativeText || '') : (this.valueFormatted);
  }

  get message(): string {
    if (this.invalid) {
      return this._message;
    } else if (this.hasContentTooltip) {
      return this._message;
    } else {
      return '';
    }
  }

  agInit(params: ICellRendererParams): void {
    this.api = params.api;
    this.column = params.column;
    this.node = params.node;
    this.params = params;
    this.modelFactory = params['modelFactory'];
    this.hasContentTooltip = coalesce(params['hasContentTooltip'], false);
    this.hasSelectTrigger = coalesce(params['hasSelectTrigger'], false);
    this.clearable = coalesce(params['clearable'] || params['forceClearable'], false);
    this.forceClearable = coalesce(params['forceClearable'], false);
    this.singleClickEdit = coalesce(params['singleClickEdit'], false);
    this.useEditableAsEnabled = coalesce(params['useEditableAsEnabled'], false);
    this.hasAlternativeText = params['hasAlternativeText'] || stubFalse;
    this.getAlternativeText = params['getAlternativeText'] || constant('');
    this.onClear = params['onClear'];
    this.editorTrigger = params['editorTrigger'];
    this.onEditorOpen = params['onEditorOpen'];

    this.refresh(params);

    this.align = params['align'] || 'left';
  }

  showMenu(): boolean {
    return this.editable && this.params.menu && this.params.menu.length && this.params.showMenu && this.params.showMenu(this.params.data);
  }

  refresh(params: any): boolean {
    const cellParams = {
      node: params.node,
      colDef: params.column.getColDef(),
      data: params.node.data,
    };
    const model = this.model = this.modelFactory(cellParams);

    this.data = params.node.data;
    this.valueFormatted = coalesce(params.valueFormatted, model.value);

    this.alternativeText = this.hasAlternativeText(cellParams) ? this.getAlternativeText(cellParams) : void 0;

    const errors = model.errors;
    if (errors) {
      const key = first(Object.keys(errors))!; // tslint:disable-line:no-non-null-assertion
      this._message = this.messageService.getText(key, errors[key]);
    } else if (this.hasContentTooltip) {
      this._message = this.valueFormatted;
    } else {
      this._message = '';
    }

    // trigger change detection as component is always displayed with OnPush change detection strategy
    this.changeDetectorRef.markForCheck();

    return true;
  }

  @HostListener('click', ['$event'])
  onClick(event: MouseEvent): void {
    if (!this.enabled) {
      return;
    }
    if (hasClosest(event.target, 'm-trigger--clear')) {
      this.onClear(this.createCellParams());
    } else if (hasClosest(event.target, 'm-editor-trigger')) {
      this.onEditorOpen(this.createCellParams());
    } else if (this.singleClickEdit) {
      this.api.startEditingCell({ rowIndex: this.node.rowIndex, colKey: this.column });
    }
  }

  private createCellParams(): CellParams {
    return { node: this.node, colDef: this.column.getColDef(), data: this.data };
  }
}
